import { getLogger } from '@williamhill/rosi-ui-shared/src/lib/logger-plus';
import config from '../config';
import { getStateDetails, getUrlBranding, retailToGeneralState, States, StateToRegion, Ticket } from '../model';
import { DynamicConfig, parseDynamicConfig } from './dynamic-config';
import { renderCaptcha } from '../aws-waf-captcha';

const sportsBookUrlPaths = {
  regions: 'regions',
  locations: 'locations',
  brands: 'brands',
  betsQuery: 'sb/bets'
};

const headers = {
  'x-platform': 'retail',
  'x-app-version': config.build.version
};

const timeout = 10 * 1000;
const logger = getLogger('bet-tracker/services/american-wagering-gateway');

export class AmericanWageringGateway {
  private baseUri: string = config.URLS.aw_api;
  private appInfoHeaders: HeadersInit;
  private dynamicConfig: DynamicConfig | undefined;
  private loadDynamicConfigPromise: Promise<unknown>;
  private timeoutId: number;

  constructor({ uniqueDeviceId }: { uniqueDeviceId: string }) {
    this.appInfoHeaders = {
      ...headers,
      'x-unique-device-id': uniqueDeviceId
    };
    this.loadDynamicConfigPromise = this.loadDynamicConfig();
  }

  async getTicketDetails({
    retailTicketId,
    abortController
  }: {
    retailTicketId: string;
    abortController: AbortController;
  }): Promise<Ticket> {
    try {
      const dynamicConfig = this.ensureDynamicConfig();
      const url = this.getTicketDetailsUrl({ retailTicketId, dynamicConfig });

      const response = await this.get({
        url,
        abortController
      });

      if (response.status === 405) {
        this.stopTimeout();
        await renderCaptcha(config.AWS.api_key);
        return this.getTicketDetails({ retailTicketId, abortController });
      }

      const data = await response.json();

      if (!response.ok) {
        throw this.parseHttpError({ response, error: data });
      }
      return data;
    } catch (error) {
      throw this.parseError({ error, abortController });
    }
  }

  private parseHttpError({
    response,
    error
  }: {
    response: Response;
    error: { message?: string; errors?: { message: string }[] };
  }) {
    if (
      response.status === 404 &&
      error.errors &&
      error.errors.length > 0 &&
      error.errors[0].message.startsWith('No bets were found for retail ticket id')
    ) {
      return new Error('Bet Not Found, check the number has been entered correctly');
    }

    return new Error(error.message || 'Unknown error');
  }

  private parseError({ error, abortController }: { error: { message?: string }; abortController: AbortController }) {
    if (abortController.signal.aborted) {
      throw new Error('Tracking bet has been cancelled');
    }
    if (!error.message || error?.message === 'Failed to fetch') {
      throw new Error('There has been a problem, please try again soon');
    }
    throw new Error(error.message);
  }

  getDynamicConfig(): DynamicConfig {
    return this.ensureDynamicConfig();
  }

  async waitForDynamicConfig() {
    await this.loadDynamicConfigPromise;
  }

  private async loadDynamicConfig() {
    const url = `${config.URLS.aw_api}/configs/${config.BET_TRACKER_CONFIG_FILE}`;
    try {
      const response = await this.get({ url });
      if (!response.ok) {
        throw new Error(response.status.toString());
      } else {
        const data = await response.text();
        this.dynamicConfig = parseDynamicConfig(data);
      }
    } catch (error) {
      logger.error(
        error,
        `Failed to get and parse configuration from '${config.URLS.aw_api}/configs/${config.BET_TRACKER_CONFIG_FILE}'`
      );
    }
  }

  private ensureDynamicConfig(): DynamicConfig {
    if (!this.dynamicConfig) {
      throw new Error('Missing dynamic config');
    }
    return this.dynamicConfig;
  }

  private get({
    url,
    headers,
    abortController = new AbortController()
  }: {
    url: string;
    headers?: Record<string, string>;
    abortController?: AbortController;
  }): Promise<Response> {
    const fetchFn = this.resolveFetch();
    this.startTimeout(abortController);
    return fetchFn(url, {
      headers: {
        ...this.appInfoHeaders,
        ...headers
      },
      signal: abortController.signal
    });
  }

  private startTimeout(abortController: AbortController) {
    this.stopTimeout();
    this.timeoutId = setTimeout(() => abortController.abort(), timeout);
  }

  private stopTimeout() {
    this.timeoutId && clearTimeout(this.timeoutId);
  }

  private resolveFetch() {
    if (window.AwsWafIntegration) {
      return window.AwsWafIntegration.fetch;
    }
    logger.info({}, 'AWS AWSWafIntegration not loaded, using native fetch');
    return window.fetch;
  }

  private getTicketDetailsUrl({
    retailTicketId,
    dynamicConfig
  }: {
    retailTicketId: string;
    dynamicConfig: DynamicConfig;
  }) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { regions, locations, brands, betsQuery } = sportsBookUrlPaths;
    const { OVERRIDE_LOCATION: overrideLocation } = dynamicConfig;

    const regionAndState = getStateDetails(retailTicketId);
    if (!regionAndState) {
      throw new Error(
        'The entered number is incorrect or bet cannot be tracked, check the number has been entered correctly'
      );
    }

    const { region, state, urlBranding } = overrideLocation
      ? {
          region: StateToRegion[overrideLocation as States] as string,
          state: overrideLocation as States,
          urlBranding: getUrlBranding(overrideLocation as States)
        }
      : regionAndState;
    const location = retailToGeneralState(state);

    return `${this.baseUri}/${regions}/${region}/${locations}/${location}/${brands}/${urlBranding}/${betsQuery}/${retailTicketId}`;
  }
}
